import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatIconModule } from '@angular/material/icon';
import type { SectionFieldConfig } from './section.types';
import { ReadSectionEmptyComponent } from './readonly/read-section-empty.component';
import { readSectionState, type ReadSectionState } from './readonly/read-section.util';

let sectionPanelId = 0;

/**
 * Structural `section` field type — a titled card that groups fields on a long form.
 *
 * Two things set it apart from `accordion` and `group`:
 *
 * 1. **The key is optional.** A section without a key groups fields on screen without becoming a
 *    level in the data model, so the fields underneath keep the paths they already had. That is
 *    what makes it usable on a create-form, where the model has to stay flat to mirror the module
 *    it writes into — an accordion's key would push every field a level down and break the mapping.
 *    Give it a key and it nests like a group does.
 * 2. **Collapsing is opt-in.** A section is a card first: on an order form the client block and the
 *    route are always open, while the blocks a planner fills in later fold away. `props.collapsible`
 *    turns the header into a button, `props.expanded` says how it starts.
 *
 * A collapsed section keeps its fields in the DOM (hidden), never removes them. Formly holds the
 * controls either way, so a required field folded out of sight still blocks saving — and if the
 * element is gone, the error list cannot scroll to it and the planner is stuck with a dead Save
 * button and nothing to click.
 */
@Component({
  selector: 'formly-field-section',
  template: `
    <!-- Read mode hid every answer in here (#21239). A card that vanishes leaves the reader unaware
         the section ever existed, so keep the heading and say so in words — the same call
         group.component.ts makes. Nested inside an already-empty section, the outer one has said
         it; a second notice adds nothing. -->
    @if (readState !== 'nested-empty' && (hasVisibleFields() || readState === 'empty')) {
      <!-- mb-6, not mb-4: at 16px the gap between two cards read as part of the card's own padding
           (20px), so a long form looked like one undivided slab instead of separate blocks. -->
      <section class="dd-section mb-6 w-full overflow-hidden rounded-xl border bg-card" data-cy="section">
        @if (props.label || props.description) {
          @if (collapsible()) {
            <h2 class="dd-section-title">
              <button
                type="button"
                class="dd-section-header flex w-full items-start gap-4 px-5 py-4 text-left select-none"
                [attr.aria-expanded]="expanded()"
                [attr.aria-controls]="panelId"
                data-cy="section-header"
                (click)="toggle()">
                <ng-container *ngTemplateOutlet="heading"></ng-container>
                <!--
                  Folded, the card is a closed drawer: nothing says whether it holds anything or
                  whether what it holds is wrong. On a form where Save is disabled, that turns a
                  blocking error into a hunt through every closed section.
                -->
                @if (!expanded()) {
                  @if (summary().invalid) {
                    <span class="dd-section-status text-warn ml-auto shrink-0" data-cy="section-status">
                      <mat-icon class="icon-size-4 align-middle" svgIcon="heroicons_outline:exclamation-circle">
                      </mat-icon>
                      Actie nodig
                    </span>
                  } @else if (summary().filled) {
                    <span class="dd-section-status dd-section-filled ml-auto shrink-0" data-cy="section-status">
                      {{ summary().filled }} ingevuld
                    </span>
                  }
                }
                <span
                  class="dd-section-chevron shrink-0 transition-transform duration-200"
                  [class.ml-auto]="expanded() || (!summary().invalid && !summary().filled)"
                  [class.ml-3]="!expanded() && (summary().invalid || summary().filled)"
                  [class.rotate-180]="expanded()">
                  <mat-icon class="icon-size-5" svgIcon="heroicons_outline:chevron-down"></mat-icon>
                </span>
              </button>
            </h2>
          } @else {
            <h2 class="dd-section-title flex w-full items-start gap-4 px-5 py-4">
              <ng-container *ngTemplateOutlet="heading"></ng-container>
            </h2>
          }
        }

        <!--
          flow-root, not a plain block: the form builder gives every child field a float-left
          className, and a block box does not grow to contain floats — the card border would close
          above its own fields. flow-root contains them without clipping, so dropdowns and tooltips
          still escape the card.

          [hidden] rather than @if: see the class comment. The fields stay in the DOM so the error
          list can still reach a required field inside a folded section.

          flow-root is bound rather than static because tailwind.config.js sets important: true.
          Every utility then lands as "display: flow-root !important", which outranks the
          unprefixed "[hidden] { display: none }" from preflight. Static, the two rules race on the
          same specificity and a folded section can render its fields anyway. Bound, a folded
          section simply has no display utility and stays folded.
        -->
        <div
          class="dd-section-body px-5 pb-5"
          [id]="panelId"
          [hidden]="!expanded()"
          [class.flow-root]="expanded()"
          [class.pt-4]="!!props.label || !!props.description"
          [class.pt-5]="!props.label && !props.description"
          [class.border-t]="!!props.label || !!props.description">
          @for (fieldItem of field.fieldGroup; track $index) {
            <formly-field [field]="fieldItem"></formly-field>
          }
          @if (readState === 'empty') {
            <dd-read-section-empty />
          }
        </div>
      </section>
    }

    <ng-template #heading>
      <span class="min-w-0 flex-1">
        @if (props.label) {
          <span class="dd-section-label block">{{ props.label }}</span>
        }
        @if (props.description) {
          <span class="dd-section-description mt-1 block">{{ props.description }}</span>
        }
      </span>
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .dd-section-title {
      margin: 0;
    }

    /*
      The hidden attribute alone does not close this panel. Its display:none comes from the UA
      stylesheet, while flow-root on the same element is an author rule — and tailwind.config.js
      sets important:true, so every utility lands as !important. Matching that is the only way to
      win; without it the collapsed sections render fully open.
    */
    .dd-section-body[hidden] {
      display: none !important;
    }

    /*
      One step ABOVE the h3 sub-headings the composite fields render inside these cards (route
      stops, the repeat blocks). At the same 16px the parent read as a child of its own content.
    */
    .dd-section-label {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.4;
    }

    /*
      --dd-accent is the design's Foreground/Secondary: #545559 in light, #c7c7cc in dark. The
      Tailwind text-accent utility is a fixed grey tuned for dark and drops to 1.68:1 on a light
      card, which fails AA for text and for the chevron as a control glyph.
    */
    .dd-section-description {
      font-size: 14px;
      line-height: 1.5;
      color: var(--dd-accent);
    }

    .dd-section-chevron {
      color: var(--dd-accent);
    }

    /* Reads as a status, not as a second heading competing with the section title. */
    .dd-section-status {
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      white-space: nowrap;
    }

    .dd-section-filled {
      color: var(--dd-accent);
    }

    /* The card edge. Tokens, never literals — a hardcoded pair silently breaks one of the themes. */
    .dd-section {
      border-color: var(--fuse-border);
    }

    /*
      The base layer removes the UA focus ring globally (tailwind.scss), and this header is a
      native button styled from scratch, so without these it has no hover or keyboard affordance.
      currentColor for the ring: dark on light, light on dark, contrast never below the text it
      sits on — where the brand green measured 2.05:1 on white.
    */
    .dd-section-header {
      transition: background-color 150ms ease;
    }

    .dd-section-header:hover {
      background-color: var(--dd-section-hover, rgba(0, 0, 0, 0.02));
    }

    :host-context(.dark) .dd-section-header {
      --dd-section-hover: rgba(255, 255, 255, 0.04);
    }

    .dd-section-header:focus-visible {
      outline: 2px solid currentColor !important;
      outline-offset: -2px;
    }
  `,
  imports: [FormlyModule, MatIconModule, NgTemplateOutlet, ReadSectionEmptyComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySectionComponent extends FieldType<SectionFieldConfig> implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #open = signal<boolean | null>(null);

  /** Evaluated per render: the answers' `hide` flags are only resolved once Formly has built them. */
  get readState(): ReadSectionState {
    return readSectionState(this.field);
  }

  /** Ties the header button to the panel it controls (ARIA Disclosure). */
  readonly panelId = `dd-section-panel-${++sectionPanelId}`;

  /** Only a collapsible section can be closed; a plain card is always open. */
  readonly collapsible = computed(() => !!this.props.collapsible);

  readonly expanded = computed(() => {
    if (!this.collapsible()) {
      return true;
    }
    // `null` until the user touches it, so a re-render keeps honouring the configured default — and
    // so a planner who folds a prefilled card away keeps it folded.
    return this.#open() ?? (this.#hasPrefill() || this.props.expanded !== false);
  });

  /**
   * Whether the caller filled anything in here. A card that starts closed on values the planner did
   * not enter hides exactly the thing they need to check: the + on a driver's 10:00 slot fills the
   * crew row, and folding that away makes the form look as though it ignored the click.
   *
   * Configured defaults must NOT open it — a crew row starts with a role and an amount of 1, which
   * would leave Toewijzing permanently open and the collapse pointless. Hence the paths from
   * DetailsService rather than a look at whether a control holds something.
   */
  readonly #hasPrefill = signal(false);

  /**
   * Whether anything inside is still on screen. A section whose fields are all conditionally
   * hidden would otherwise render as a titled strip with nothing under it, which reads as a
   * rendering fault — an order form shows exactly that for its repeat block while the order is
   * a one-off.
   *
   * Recomputed on form value changes because `hide` is an expression over the model, not a signal;
   * `field.fieldGroup` itself never changes identity.
   */
  readonly #visibleFields = signal(true);
  readonly hasVisibleFields = this.#visibleFields.asReadonly();

  /**
   * What a folded card reports about itself: how many of its fields hold a value, and whether any
   * of them is blocking the save. Only read while collapsed — open, the fields speak for themselves.
   */
  readonly #summary = signal<SectionSummary>({ filled: 0, invalid: false });
  readonly summary = this.#summary.asReadonly();

  ngOnInit(): void {
    // Formly assigns `field` (and with it `form`) after construction, so this waits for OnInit.
    this.#hasPrefill.set(containsPrefill(this.field, this.options?.formState?.prefilledPaths));
    this.#recompute();
    // `events` rather than `valueChanges`/`statusChanges`: touching a field or submitting the form
    // changes neither a value nor (for an already-invalid control) the status, yet it is what turns
    // the folded card's "Actie nodig" on.
    this.form.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#recompute());
  }

  toggle(): void {
    this.#open.set(!this.expanded());
  }

  #recompute(): void {
    const children = this.field?.fieldGroup ?? [];
    // No children at all is a section an admin has not filled in yet: keep it, so the card does
    // not vanish from the builder preview the moment it is created.
    this.#visibleFields.set(children.length === 0 || children.some(child => !child.hide));
    this.#summary.set(summarise(children));
  }
}

interface SectionSummary {
  filled: number;
  invalid: boolean;
}

/**
 * Whether any field under this section sits on a path the caller prefilled.
 *
 * Matched on `props.keyPath` minus its `entry.` head, which is the same spelling DetailsService
 * collects. A hidden field does not count: a condition switched it off, so its value is not on
 * screen and opening the card would show nothing.
 */
function containsPrefill(field: SectionFieldConfig | undefined, prefilledPaths: unknown): boolean {
  const paths = prefilledPaths instanceof Set ? (prefilledPaths as Set<string>) : undefined;
  if (!paths?.size) {
    return false;
  }

  const walk = (fields: SectionFieldConfig['fieldGroup']): boolean =>
    (fields ?? []).some(child => {
      if (child.hide) {
        return false;
      }
      const keyPath = child.props?.['keyPath'];
      if (typeof keyPath === 'string' && overlapsPrefill(keyPath.replace(/^entry\./, ''), paths)) {
        return true;
      }
      return walk(child.fieldGroup);
    });

  return walk(field?.fieldGroup);
}

/**
 * Whether a field's path and a prefilled path touch the same data, in either direction.
 *
 * Exact equality is too strict from inside a section, which cannot know how the form nests: a
 * prefill of `users` (one array of crew rows) has to open a card whose fields are `users.role` and
 * `users.users.user`, and a prefill of `users.users.user` has to open a card holding the `users`
 * repeat. Segment boundaries are respected, so `user` never matches `users`.
 */
function overlapsPrefill(keyPath: string, paths: Set<string>): boolean {
  if (paths.has(keyPath)) {
    return true;
  }
  for (const path of paths) {
    if (keyPath.startsWith(`${path}.`) || path.startsWith(`${keyPath}.`)) {
      return true;
    }
  }
  return false;
}

/**
 * Walks the visible fields under a section and reports what a planner would see if it were open.
 *
 * Hidden branches are skipped: a field a condition switched off is neither filled in nor a problem,
 * and counting it would make a folded card claim values the planner never entered.
 *
 * `touched` gates the error, not `invalid` alone — an untouched required field is simply empty, and
 * flagging a card red before anyone typed reads as broken rather than helpful. A submit attempt
 * marks everything touched, which is exactly when the flag earns its place.
 *
 * `dirty` gates the count for the same reason: a module's configured defaults (a crew row starting
 * at Chauffeur, an amount of 1) are values, but they are not something the planner entered. Counting
 * them made a freshly opened order announce "6 ingevuld" before anyone had typed a character.
 * Angular's CVA pipeline is what sets `dirty`, so this counts user edits and not hydration — the
 * same distinction commitUserValue draws for the save button.
 */
function summarise(fields: SectionFieldConfig['fieldGroup'], summary: SectionSummary = { filled: 0, invalid: false }) {
  for (const field of fields ?? []) {
    if (field.hide) {
      continue;
    }
    if (field.fieldGroup?.length) {
      summarise(field.fieldGroup, summary);
      continue;
    }
    const control = field.formControl;
    if (!control) {
      continue;
    }
    if (control.dirty && hasValue(control.value)) {
      summary.filled++;
    }
    if (control.invalid && control.touched) {
      summary.invalid = true;
    }
  }
  return summary;
}

/** Empty string, empty array and empty object all mean "not filled in" to a planner. */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && !(value instanceof Date)) {
    return Object.values(value as Record<string, unknown>).some(hasValue);
  }
  return true;
}
